// @flow

import * as mobx from 'mobx'
import * as React from 'react'
import PT from 'prop-types'
import cx from 'classnames'
import moment from 'moment'
import { Observable } from 'rxjs/Observable'
import logger from '@vimcar/shared-utils/logger'
import { filterTrueTuples } from '@vimcar/shared-utils/utils'
import { type Params, type InitializerViewModel } from '@vimcar/shared-utils/linkViewModel'
import { type Users } from '@vimcar/shared-stores/mobx/users'
import { type DomainSwitch } from '@vimcar/shared-stores/mobx/DomainSwitch'
import { type Booking } from '@vimcar/shared-stores/mobx/Calendar/Bookings/Booking'
import { type Calendar } from '@vimcar/shared-stores/mobx/Calendar'
import { type Localization } from '@vimcar/shared-components/components/LocalizationProvider'

import { type Kiosk } from '#components/KioskProvider'
import { isLtEnd, isLtStart } from '#utils/calendar'
import {
  SHOW_WALKTHROUGH_TIMEOUT,
  BOOKING_MODIFICATION_KEY,
} from '#utils/booking'
import { O } from '#utils/rx'
import i18n from '#utils/i18n'
import {
  BREAKPOINT_MOBILE,
  BREAKPOINT_DESKTOP,
} from '#utils/ui'
import styles from './styles.css'

const tp = i18n.prefix('components.BookingsOverview')

const ALL = 'all'
const PAST = 'past'
const CURRENT = 'current'
const FUTURE = 'future'
const CANCELED = 'canceled'
const PENDING = 'pending'

const DEFAULT_VIEW = FUTURE

type Row = {
  nickname: string,
  makeModel: string,
  id: string,
  licenseplate: string,
  start: string,
  end: string,
  timeframe: [string, string],
  comment: ?string,
}

type FilterAction = {
  active: boolean,
  label: string,
  onClick: () => void,
}

export type Props = {}

export type Context = {
  persistentStore: { values: mobx.ObservableMap<boolean> },
  users: Users,
  viewport: { is: (string) => boolean },
  calendar: Calendar,
  domain: DomainSwitch<any>,
  kiosk: Kiosk,
  localization: Localization,
}

type CurrentBooking = {
  formattedEnd: string,
  formattedStart: string,
  id: string,
  licenseplate: string,
  makeModel: string,
  name: string,
}

type _ViewModel = {|
  _bookingApprovalNeeded: boolean,
  view: string,
  error: string | null,
  showDeleteBookingDialog: boolean,
  showEndBookingDialog: boolean,
  currentBooking: CurrentBooking | null,
  columns: Array<Object>,
  filterPaths: Array<string>,
  menuablePluginOptions: (Object) => Object,
  filterActions: Array<FilterAction>,
  activeFilterLabel: ?string,
  isKioskMode: boolean,
  showOnlySubscibedVehicles: boolean,
  bookings: Array<Booking>,
  rows: Array<Row>,
  _showWalkThrough: boolean,
  showWalkThrough: boolean,
  walkThroughSteps: Array<{
    title: string,
    text: string,
    position: string,
    selector: string,
  }>,
  handleShowDeleteBookingDialog: (Row) => void,
  handleShowEndBookingDialog: (Row) => void,
  handleConfirmDeleteBooking: () => void,
  handleConfirmEndBooking: () => void,
  handleCancelDeleteBooking: () => void,
  handleCancelEndBooking: () => void,
  showPendingTab: boolean,
  setView: (string) => void,
  handleWalkThroughClose: ({ action: string }) => void,
  componentDidMount: () => void,
  componentWillUnmount: () => void,
|}

export type ViewModel = {
  ...InitializerViewModel<any>,
  ..._ViewModel,
}

const mkViewModel = (
  { registerObservable, initializer }: Params,
  props: Props,
  ctx: Context,
): ViewModel => initializer(
  () => {
    const inventories = ctx.calendar.inventories
    if (!inventories) throw new Error('Loading bookings failed')
    return Observable.forkJoin(
      inventories.list(),
      ctx.calendar.list(ALL, getShowOnlySubscibedVehicles(ctx))
    )
  },
  (_vm) => {
    let walkthroughTimeout = null
    const bookings = ctx.calendar.bookings
    if (!bookings) throw new Error('Loading bookings failed')

    const { formatTime, formatTimeframe } = mkTimeFormatters(ctx.localization)

    const vm: ViewModel = mobx.extendObservable(_vm, ({
      view: hasPending(ctx)
        ? PENDING
        : DEFAULT_VIEW,

      error: null,

      showEndBookingDialog: false,

      showDeleteBookingDialog: false,

      currentBooking: null,

      get columns() {
        const columns = [{
          property: 'licenseplate',
          header: { label: tp('licenseplate') },
        },
        {
          property: 'makeModel',
          header: { label: tp('make-model') },
        }]

        if (ctx.viewport.is(BREAKPOINT_MOBILE)) {
          columns.push({
            property: 'timeframe',
            header: {
              label: tp('timeframe'),
              props: {
                className: styles.increaseWidth,
              },
            },
            cell: {
              props: {
                className: cx(
                  styles.increaseWidth,
                  styles.timeframe
                ),
              },
              transforms: [formatTimeframe],
            },
          })
        } else if (ctx.viewport.is(BREAKPOINT_DESKTOP)) {
          columns.push({
            property: 'nickname',
            header: { label: tp('nickname') },
          }, {
            property: 'start',
            header: { label: tp('start') },
            plugins: { sortable: {
              getValue: (t) => +moment.utc(t.start),
              order: 'desc',
            } },
            cell: { transforms: [formatTime] },
          }, {
            property: 'end',
            header: { label: tp('end') },
            cell: { transforms: [formatTime] },
            plugins: { sortable: {
              getValue: (t) => +moment.utc(t.end),
            } },
          }, {
            property: 'comment',
            header: { label: tp('comment') },
          })
        }

        return columns
      },

      filterPaths: mobx.observable.ref([
        ['licenseplate'],
        ['makeModel'],
        ['nickname'],
        ['comment'],
      ]),

      menuablePluginOptions: (menuStyles) => ({
        menuProps: {
          className: styles.walkthroughRef,
          targetOrigin: { horizontal: 'right', vertical: 'top' },
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        },
        menuItems: (row) => {
          const now = moment.utc()
          const deletable =
            isLtStart(now)({
              duration: {
                start: row.start,
                end: row.end,
              },
            })
          const endable =
            !deletable &&
            isLtEnd(now)({
              duration: {
                start: row.start,
                end: row.end,
              },
            })

          return [{
            className: endable
              ? undefined
              : menuStyles.menuItemDisabled,
            primaryText: tp('end-booking-action'),
            onClick: () => {
              if (!endable) return
              vm.handleShowEndBookingDialog(row)
            },
          }, {
            className: deletable
              ? menuStyles.delete
              : menuStyles.menuItemDisabled,
            primaryText: tp('cancel-booking'),
            onClick: () => {
              if (!deletable) return
              vm.handleShowDeleteBookingDialog(row)
              return false
            },
          }]
        },
      }),

      get isKioskMode() {
        return ctx.kiosk.isKioskMode
      },

      get showOnlySubscibedVehicles() {
        return getShowOnlySubscibedVehicles(ctx)
      },

      get bookings() {
        const _filteredBookings = vm.showOnlySubscibedVehicles
          ? bookings.ownAccessible
          : bookings.own
        switch (vm.view) {
          case PAST:
            return _filteredBookings.past
          case FUTURE:
            return _filteredBookings.future
          case CURRENT:
            return _filteredBookings.current
          case CANCELED:
            return _filteredBookings.canceled
          case PENDING:
            return _filteredBookings.pending
          case ALL:
          default:
            return _filteredBookings.all
        }
      },

      get rows() {
        return vm.bookings
          .map(({ inventory, urn, duration, comment }) => ({
            nickname: inventory ? inventory.name : '',
            makeModel: inventory && inventory.details
              ? `${inventory.details.make} ${inventory.details.model}`
              : '',
            id: urn.id,
            licenseplate: inventory && inventory.details
              ? inventory.details.licenseplate
              : '',
            start: duration.start.iso,
            end: duration.end.iso,
            timeframe: [duration.start.iso, duration.end.iso],
            comment,
          }))
      },

      _showWalkThrough: false,

      get showWalkThrough() {
        return (
          vm.view === FUTURE &&
          vm.rows.length > 0 &&
          vm._showWalkThrough
        )
      },

      get walkThroughSteps() {
        return [{
          title: tp('walkthrough-title'),
          text: tp('walkthrough-text'),
          position: 'bottom-right',
          selector: `.${styles.walkthroughRef}`,
        }]
      },

      handleShowDeleteBookingDialog: mobx.action((row) => {
        const booking = bookings.map.get(row.id)
        vm.currentBooking = {
          ...row,
          formattedStart: formatTime(row.start),
          formattedEnd: formatTime(row.end),
          name: booking && booking.owner
            ? `${booking.owner.firstname} ${booking.owner.lastname}`
            : '',
        }
        vm.showDeleteBookingDialog = true
      }),

      handleShowEndBookingDialog: mobx.action((row) => {
        const booking = bookings.map.get(row.id)
        vm.currentBooking = {
          ...row,
          formattedStart: formatTime(row.start),
          formattedEnd: formatTime(row.end),
          name: booking && booking.owner
            ? `${booking.owner.firstname} ${booking.owner.lastname}`
            : '',
        }
        vm.showEndBookingDialog = true
      }),

      handleConfirmDeleteBooking: mobx.action(() => {
        const booking = vm.currentBooking

        if (booking) {
          registerObservable(
            bookings.delete(booking.id)
              .catch(mobx.action(() => {
                vm.error = tp('failed-to-delete-booking')
                return O.empty()
              }))
              .do(() => {
                vm.handleCancelDeleteBooking()
              })
          )
        }
      }),

      handleConfirmEndBooking: mobx.action(() => {
        const booking = vm.currentBooking &&
          bookings.map.get(vm.currentBooking.id)

        if (!booking) return

        registerObservable(
          booking.terminate()
            .catch(mobx.action((err) => {
              logger.error(err)
              vm.error = tp('failed-to-end-booking')
              return O.empty()
            }))
            .do(() => {
              vm.handleCancelEndBooking()
            })
        )
      }),

      handleCancelDeleteBooking: mobx.action(() => {
        vm.showDeleteBookingDialog = false
        vm.currentBooking = null
      }),

      handleCancelEndBooking: mobx.action(() => {
        vm.showEndBookingDialog = false
        vm.currentBooking = null
      }),

      get _bookingApprovalNeeded() {
        return Boolean(
          ctx.domain.currentDomain.settings &&
          ctx.domain.currentDomain.settings.map &&
          ctx.domain.currentDomain.settings.map.booking.bookingApproval
        )
      },

      get showPendingTab() {
        return hasPending(ctx) || vm._bookingApprovalNeeded
      },

      get filterActions() {
        return filterTrueTuples([
          [vm.showPendingTab, {
            active: vm.view === PENDING,
            label: tp('filter-pending'),
            onClick: () => vm.setView(PENDING),
          }],
          [true, {
            active: vm.view === ALL,
            label: tp('filter-all'),
            onClick: () => vm.setView(ALL),
          }],
          [true, {
            active: vm.view === CURRENT,
            label: tp('filter-current'),
            onClick: () => vm.setView(CURRENT),
          }],
          [true, {
            active: vm.view === PAST,
            label: tp('filter-past'),
            onClick: () => vm.setView(PAST),
          }],
          [true, {
            active: vm.view === FUTURE,
            label: tp('filter-future'),
            onClick: () => vm.setView(FUTURE),
          }],
          [true, {
            active: vm.view === CANCELED,
            label: tp('filter-canceled'),
            onClick: () => vm.setView(CANCELED),
          }],
        ])
      },

      get activeFilterLabel() {
        const item = vm.filterActions.find((a) => a.active)
        return item ? item.label : undefined
      },

      setView: mobx.action((newView) => {
        vm.view = newView

        registerObservable(
          ctx.calendar.list(newView, vm.showOnlySubscibedVehicles)
            .catch(mobx.action((err) => {
              logger.error(err)
              vm.error = tp('failed-to-get-bookings')
              return O.empty()
            }))
        )
      }),

      handleWalkThroughClose: mobx.action((callback) => {
        if (callback.action === 'close') {
          ctx.persistentStore.values.set(BOOKING_MODIFICATION_KEY, true)
        }
      }),

      componentDidMount() {
        if (
          ctx.viewport.is(BREAKPOINT_DESKTOP) &&
          ctx.persistentStore.values.get(BOOKING_MODIFICATION_KEY) === undefined
        ) {
          walkthroughTimeout = window.setTimeout(
            mobx.action(() => {
              vm._showWalkThrough = true
            }),
            SHOW_WALKTHROUGH_TIMEOUT
          )
        }
      },

      componentWillUnmount() {
        window.clearTimeout(walkthroughTimeout)
      },

    }: _ViewModel))

    return vm
  }
)

mkViewModel.contextTypes = {
  calendar: PT.object,
  kiosk: PT.object,
  localization: PT.object,
  persistentStore: PT.object,
  users: PT.object,
  domain: PT.object,
  viewport: PT.object,
}

const mkTimeFormatters = (localization: Localization) => {
  const formatTime = (isoTime: string): string =>
    localization.formatCustomDate(
      `L\u00a0LT\u00a0[${i18n.t('global.clock')}]`,
      isoTime
    )

  return {
    formatTime,
    formatTimeframe: ([start, end]: [string, string]): React.Node => (
      <div>
        {formatTime(start)}<br/>
        {formatTime(end)}
      </div>
    ),
  }
}

/*
  Warning: naming mismatch! Please note that we are deprecating subscriptions
  from our codebase. The "unsubscribedCarsVisibleBooking" property remains as is,
  because it already lives in customers' data. The term "Subscribed"
  means that a "user" has direct permissions (at least "read") to a particular
  vehicle. So this settings switches between "see all" vs "see only accessible for me"
  vehicles. In other places we [will] use the term "accessible" instead of "subscribed"
*/
const getShowOnlySubscibedVehicles = (ctx: Context): boolean => !!(
  ctx.domain.currentDomain.settings.map &&
  !ctx.domain
    .currentDomain
    .settings.map
    .booking.unsubscribedCarsVisibleBooking
)

const hasPending = (ctx: Context): boolean => {
  const bookings = ctx.calendar.bookings
  if (!bookings) return false

  return getShowOnlySubscibedVehicles(ctx)
    ? bookings.ownAccessible.pending.length > 0
    : bookings.own.pending.length > 0
}
export default mkViewModel
